import api from './api';

// ============================================
// TYPES
// ============================================

export interface LeaveType {
  _id: string;
  name: string;
  code: string;
  description?: string;
  isPaid: boolean;
  color: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LeaveAllocation {
  leaveTypeId: string | { _id: string; name: string; code: string; color: string };
  annualQuota: number;
  carryForwardLimit: number;
  maxConsecutiveDays: number;
}

export interface LeavePolicy {
  _id: string;
  name: string;
  description?: string;
  leaveAllocations: LeaveAllocation[];
  roleIds: (string | { _id: string; name: string })[];
  cycleStartMonth: number;
  enableSandwichRule?: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type LeaveRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'REVOKED';

export interface LeaveRequest {
  _id: string;
  employeeId: string | { _id: string; name: string; email?: string; userCode?: string };
  leaveTypeId: string | { _id: string; name: string; code: string; color: string };
  startDate: string;
  endDate: string;
  totalDays: number;
  reason: string;
  status: LeaveRequestStatus;
  approvedBy?: string | { _id: string; name: string };
  approvedAt?: string;
  rejectedBy?: string | { _id: string; name: string };
  rejectedAt?: string;
  rejectionReason?: string;
  revokedBy?: string | { _id: string; name: string };
  revokedAt?: string;
  revocationReason?: string;
  lastEditedBy?: string | { _id: string; name: string };
  lastEditedAt?: string;
  editReason?: string;
  // Snapshot of the days that were deducted, frozen at submission. Used by
  // the Monthly Leave Usage chart so policy/holiday changes don't
  // retroactively re-bucket past leaves.
  chargedDays?: string[];
  appliedSandwichRule?: boolean;
  cycleYear: number;
  createdAt: string;
  updatedAt: string;
}

export interface LeaveBalance {
  _id: string;
  leaveTypeId: { _id: string; name: string; code: string; color: string; isPaid: boolean };
  cycleYear: number;
  allocated: number;
  used: number;
  pending: number;
  carryForward: number;
  available: number;
  maxConsecutiveDays: number;
  carryForwardLimit: number;
}

export interface MyLeaveBalanceResponse {
  policy: {
    name: string;
    cycleStartMonth: number;
  } | null;
  balances: LeaveBalance[];
  cycleYear: number;
  cycleYearDisplay: string;
}

export interface LeaveAnalyticsResponse {
  cycleYear: number;
  cycleYearDisplay: string;
  totalUsed: number;
  pendingRequests: number;
  monthlyTrend: { month: string; days: number }[];
  typeBreakdown: { name: string; code: string; color: string; days: number }[];
  recentRequests: LeaveRequest[];
}

// ============================================
// LEAVE TYPES API
// ============================================

export const getLeaveTypes = async (): Promise<LeaveType[]> => {
  const response = await api.get('/leave/types');
  return response.data.data;
};

export const createLeaveType = async (data: {
  name: string;
  code: string;
  description?: string;
  isPaid?: boolean;
  color?: string;
}): Promise<LeaveType> => {
  const response = await api.post('/leave/types', data);
  return response.data.data;
};

export const updateLeaveType = async (
  id: string,
  data: Partial<{ name: string; code: string; description: string; isPaid: boolean; color: string }>
): Promise<LeaveType> => {
  const response = await api.put(`/leave/types/${id}`, data);
  return response.data.data;
};

export const deleteLeaveType = async (id: string): Promise<void> => {
  await api.delete(`/leave/types/${id}`);
};

// ============================================
// LEAVE POLICIES API
// ============================================

export const getLeavePolicies = async (): Promise<LeavePolicy[]> => {
  const response = await api.get('/leave/policies');
  return response.data.data;
};

export const createLeavePolicy = async (data: {
  name: string;
  description?: string;
  leaveAllocations?: { leaveTypeId: string; annualQuota: number; carryForwardLimit: number; maxConsecutiveDays: number }[];
  roleIds?: string[];
  cycleStartMonth?: number;
}): Promise<LeavePolicy> => {
  const response = await api.post('/leave/policies', data);
  return response.data.data;
};

export const updateLeavePolicy = async (
  id: string,
  data: Partial<{
    name: string;
    description: string;
    leaveAllocations: { leaveTypeId: string; annualQuota: number; carryForwardLimit: number; maxConsecutiveDays: number }[];
    roleIds: string[];
    cycleStartMonth: number;
  }>
): Promise<LeavePolicy> => {
  const response = await api.put(`/leave/policies/${id}`, data);
  return response.data.data;
};

export const deleteLeavePolicy = async (id: string): Promise<void> => {
  await api.delete(`/leave/policies/${id}`);
};

// ============================================
// LEAVE REQUESTS API
// ============================================

export const getMyLeaveRequests = async (params?: {
  status?: string;
  year?: number;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<{ data: LeaveRequest[]; pagination: { total: number; page: number; limit: number; totalPages: number } }> => {
  const response = await api.get('/leave/requests/my', { params });
  return { data: response.data.data, pagination: response.data.pagination };
};

export const createLeaveRequest = async (data: {
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  reason: string;
}): Promise<LeaveRequest> => {
  const response = await api.post('/leave/requests', data);
  return response.data.data;
};

export const cancelLeaveRequest = async (id: string): Promise<void> => {
  await api.delete(`/leave/requests/${id}`);
};

// ============================================
// LEAVE APPROVAL API
// ============================================

export const getLeaveRequestsForApproval = async (params?: {
  status?: string;
  employeeId?: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<{ data: LeaveRequest[]; pagination: { total: number; page: number; limit: number; totalPages: number } }> => {
  const response = await api.get('/leave/requests/approval', { params });
  return { data: response.data.data, pagination: response.data.pagination };
};

export const approveLeaveRequest = async (id: string): Promise<LeaveRequest> => {
  const response = await api.put(`/leave/requests/${id}/approve`);
  return response.data.data;
};

export const rejectLeaveRequest = async (id: string, rejectionReason?: string): Promise<LeaveRequest> => {
  const response = await api.put(`/leave/requests/${id}/reject`, { rejectionReason });
  return response.data.data;
};

// Revoke an already-approved leave. Refunds balance.used on the server side.
export const revokeLeaveRequest = async (id: string, revocationReason?: string): Promise<LeaveRequest> => {
  const response = await api.put(`/leave/requests/${id}/revoke`, { revocationReason });
  return response.data.data;
};

// Edit the date range of an approved leave. Server recomputes totalDays and
// adjusts balance.used by the delta; returns the updated request.
export const editApprovedLeaveDates = async (
  id: string,
  payload: { startDate: string; endDate: string; editReason?: string }
): Promise<LeaveRequest> => {
  const response = await api.patch(`/leave/requests/${id}/dates`, payload);
  return response.data.data;
};

// ============================================
// LEAVE BALANCE & ANALYTICS API
// ============================================

export const getMyLeaveBalance = async (): Promise<MyLeaveBalanceResponse> => {
  const response = await api.get('/leave/balance/my');
  return response.data.data;
};

export const getMyLeaveAnalytics = async (): Promise<LeaveAnalyticsResponse> => {
  const response = await api.get('/leave/analytics/my');
  return response.data.data;
};

const leaveService = {
  // Leave Types
  getLeaveTypes,
  createLeaveType,
  updateLeaveType,
  deleteLeaveType,
  // Leave Policies
  getLeavePolicies,
  createLeavePolicy,
  updateLeavePolicy,
  deleteLeavePolicy,
  // Leave Requests
  getMyLeaveRequests,
  createLeaveRequest,
  cancelLeaveRequest,
  // Leave Approval
  getLeaveRequestsForApproval,
  approveLeaveRequest,
  rejectLeaveRequest,
  revokeLeaveRequest,
  editApprovedLeaveDates,
  // Leave Balance & Analytics
  getMyLeaveBalance,
  getMyLeaveAnalytics,
};

export default leaveService;
